export const LOCALES = ['nl', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'nl';

export const ROLES = ['owner', 'admin', 'editor', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

/** Higher number = more privileges. */
export const ROLE_RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2, owner: 3 };

export function roleAtLeast(role: Role, required: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

export const TRANSITIONS = [
  'none',
  'fade',
  'slide-left',
  'slide-right',
  'slide-up',
  'slide-down',
  'zoom',
  'push',
  'flip',
  'dissolve',
] as const;
export type Transition = (typeof TRANSITIONS)[number];

export const ELEMENT_ANIMATIONS = [
  'none',
  'fade-in',
  'fly-in-left',
  'fly-in-right',
  'fly-in-top',
  'fly-in-bottom',
  'zoom-in',
  'bounce',
  'typewriter',
] as const;
export type ElementAnimation = (typeof ELEMENT_ANIMATIONS)[number];

export const WIDGET_TYPES = [
  'clock',
  'date',
  'weather',
  'rss',
  'ticker',
  'countdown',
  'qr',
  'webpage',
  'youtube',
  'vimeo',
  'google-slides',
  'sheet-table',
  'social-wall',
  'queue',
  'menu',
  'holidays',
  'dashboard-url',
  'html',
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

export const MEDIA_ACCEPT = {
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/svg+xml'],
  gif: ['image/gif'],
  video: ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'],
  pdf: ['application/pdf'],
  pptx: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  audio: ['audio/mpeg', 'audio/mp3'],
} as const;

export const MEDIA_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'avif',
  'svg',
  'gif',
  'mp4',
  'mov',
  'webm',
  'mkv',
  'pdf',
  'pptx',
  'mp3',
] as const;

export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

export const HEARTBEAT_INTERVAL_MS = 30_000;
export const POLL_INTERVAL_MS = 60_000;
export const OFFLINE_AFTER_MS = 2 * 60_000;
export const OFFLINE_ALERT_AFTER_MS = 15 * 60_000;
export const PAIRING_CODE_LENGTH = 6;
export const PAIRING_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I

export const SCREEN_COMMANDS = [
  'reload',
  'clear-cache',
  'screenshot',
  'identify',
  'display-on',
  'display-off',
  'reboot',
  'update',
] as const;
export type ScreenCommand = (typeof SCREEN_COMMANDS)[number];

export const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

export const ASPECTS: Record<string, { width: number; height: number }> = {
  '16:9': { width: 1920, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
  '4:3': { width: 1440, height: 1080 },
};

/** Public "try the editor" sandbox (reset hourly by the worker). */
export const SANDBOX_ORG_SLUG = 'editor-sandbox';
export const SANDBOX_EMAIL = 'sandbox@tbcast.local';
/** Global template the sandbox starts from after every reset. */
export const SANDBOX_TEMPLATE_ID = 'tpl_horeca_breakfast';
